import PropTypes from "prop-types";
import { forwardRef, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// material-ui
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project imports
import useConfig from "hooks/useConfig";
import NavItem from "../NavItem";

// redux
import { useDispatch, useSelector } from "store";
import { activeItem, setActiveGroup } from "store/slices/menu";

// assets
import { IconChevronDown } from "@tabler/icons";

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { borderRadius } = useConfig();

  const { activeGroup } = useSelector((state) => state.menu);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const handleClick = () => {
    setOpen(!open);
    setSelected(!selected ? menu.id : null);

    dispatch(setActiveGroup(menu.id));

    const childrens = menu.children ? menu.children : [];
    dispatch(activeItem([childrens[0].id]));
  };

  const { pathname } = useLocation();

  useEffect(() => {
    if (activeGroup === menu.id) {
      setOpen(true);
    }
  }, [activeGroup, menu.id]);

  useEffect(() => {
    const childrens = menu.children ? menu.children : [];
    childrens.forEach((item) => {
      if (pathname && pathname.includes("product-details")) {
        if (item.url && item.url.includes("product-details")) {
          setOpen(true);
        }
      }
      if (item.url === pathname) {
        setOpen(true);
      }
    });
  }, [pathname, menu.children]);

  // menu collapse & item
  const menus = menu.children?.map((item) => {
    switch (item.type) {
      case "collapse":
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case "item":
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon strokeWidth={1.5} size="1.3rem" style={{ marginTop: "auto", marginBottom: "auto" }} />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: activeGroup === menu.id ? 8 : 6,
        height: activeGroup === menu.id ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  let itemTarget = "_self";
  if (menu.children[0].target) {
    itemTarget = "_blank";
  }

  const listItemProps = {
    component: forwardRef((props, ref) => <Link ref={ref} {...props} to={menu.children[0]?.url} target={itemTarget} />),
  };

  return (
    <>
      <ListItemButton
        {...listItemProps}
        sx={{
          borderRadius: `${borderRadius}px`,
          mb: 0.5,
          alignItems: "flex-start",
          backgroundColor: level > 1 ? "transparent !important" : "inherit",
          py: level > 1 ? 1 : 1.25,
          pl: `${level * 24}px`,
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
            transform: "translateX(4px)",
            "& .MuiListItemIcon-root": {
              transform: "scale(1.1)",
            },
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.mode === "dark" ? "rgba(144, 202, 249, 0.08)" : "rgba(25, 118, 210, 0.08)",
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(144, 202, 249, 0.12)" : "rgba(25, 118, 210, 0.12)",
            },
          },
        }}
        selected={activeGroup === menu.id}
        onClick={handleClick}>
        <ListItemIcon
          sx={{
            my: "auto",
            minWidth: !menu.icon ? 18 : 36,
            transition: "transform 0.2s ease",
            color: activeGroup === menu.id ? theme.palette.primary.main : "inherit",
          }}>
          {menuIcon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant={activeGroup === menu.id ? "h5" : "body1"}
              color="inherit"
              sx={{
                my: "auto",
                fontWeight: activeGroup === menu.id ? 600 : 400,
                transition: "all 0.2s ease",
              }}>
              {menu.title}
            </Typography>
          }
          secondary={
            menu.caption && (
              <Typography
                variant="caption"
                sx={{
                  ...theme.typography.subMenuCaption,
                  opacity: 0.7,
                }}
                display="block"
                gutterBottom>
                {menu.caption}
              </Typography>
            )
          }
        />
        <Box
          sx={{
            transition: "transform 0.3s ease",
            transform: open && activeGroup === menu.id ? "rotate(180deg)" : "rotate(0deg)",
            color: activeGroup === menu.id ? theme.palette.primary.main : "inherit",
          }}>
          <IconChevronDown stroke={1.5} size="1rem" style={{ marginTop: "auto", marginBottom: "auto" }} />
        </Box>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {open && activeGroup === menu.id && (
          <List
            component="div"
            disablePadding
            sx={{
              position: "relative",
              "&:before": {
                content: "''",
                position: "absolute",
                left: "32px",
                top: 0,
                height: "100%",
                width: "2px",
                background: `linear-gradient(to bottom, ${theme.palette.primary.light}, transparent)`,
                borderRadius: "1px",
                opacity: 0.6,
              },
            }}>
            {menus}
          </List>
        )}
      </Collapse>
    </>
  );
};

NavCollapse.propTypes = {
  menu: PropTypes.object,
  level: PropTypes.number,
};

export default NavCollapse;
